const SIGNATURES = {
  pdf: [[0x25, 0x50, 0x44, 0x46]], // %PDF
  png: [[0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]],
  jpeg: [[0xff, 0xd8, 0xff]],
  gif: [
    [0x47, 0x49, 0x46, 0x38, 0x37, 0x61],
    [0x47, 0x49, 0x46, 0x38, 0x39, 0x61],
  ],
  zip: [[0x50, 0x4b, 0x03, 0x04]],
  ole: [[0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1]],
};

export const FILE_RULES = {
  image: {
    extensions: [".png", ".jpg", ".jpeg", ".gif"],
    mimeTypes: ["image/png", "image/jpeg", "image/gif"],
    signatures: [...SIGNATURES.png, ...SIGNATURES.jpeg, ...SIGNATURES.gif],
    maxBytes: 5 * 1024 * 1024,
  },
  signature: {
    extensions: [".png", ".jpg", ".jpeg"],
    mimeTypes: ["image/png", "image/jpeg"],
    signatures: [...SIGNATURES.png, ...SIGNATURES.jpeg],
    maxBytes: 2 * 1024 * 1024,
    maxWidth: 90,
    maxHeight: 60,
  },
  pdf: {
    extensions: [".pdf"],
    mimeTypes: ["application/pdf"],
    signatures: SIGNATURES.pdf,
    maxBytes: 20 * 1024 * 1024,
  },
  spreadsheet: {
    extensions: [".xlsx", ".xls"],
    mimeTypes: [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
      "application/octet-stream",
    ],
    signatures: [...SIGNATURES.zip, ...SIGNATURES.ole],
    maxBytes: 10 * 1024 * 1024,
  },
};

const matchesSignature = (bytes, signature) =>
  signature.every((byte, index) => bytes[index] === byte);

const readHeader = (file, length = 16) =>
  file
    .slice(0, length)
    .arrayBuffer()
    .then((buffer) => new Uint8Array(buffer));

const validateImageDimensions = (file, rules) =>
  new Promise((resolve) => {
    if (!rules.maxWidth && !rules.maxHeight) return resolve(null);
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      if (rules.maxWidth && image.naturalWidth > rules.maxWidth)
        return resolve(`Image width must not exceed ${rules.maxWidth}px.`);
      if (rules.maxHeight && image.naturalHeight > rules.maxHeight)
        return resolve(`Image height must not exceed ${rules.maxHeight}px.`);
      resolve(null);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      resolve("The image could not be decoded.");
    };
    image.src = url;
  });

export const validateFile = async (file, rules) => {
  if (!file) return { valid: false, error: "Please select a file." };
  const extension = `.${file.name.split(".").pop()?.toLowerCase()}`;
  if (!rules.extensions.includes(extension)) {
    return { valid: false, error: `Unsupported file extension: ${extension}` };
  }
  if (file.size === 0 || file.size > rules.maxBytes) {
    return {
      valid: false,
      error: `File must be between 1 byte and ${Math.round(rules.maxBytes / 1024 / 1024)} MB.`,
    };
  }
  if (file.type && !rules.mimeTypes.includes(file.type)) {
    return {
      valid: false,
      error: "The file MIME type does not match its extension.",
    };
  }
  const header = await readHeader(file);
  if (
    !rules.signatures.some((signature) => matchesSignature(header, signature))
  ) {
    return {
      valid: false,
      error: "The file content does not match its declared type.",
    };
  }
  // const dimensionError = await validateImageDimensions(file, rules);
  // return dimensionError
  //   ? { valid: false, error: dimensionError }
  //   : { valid: true };

  return { valid: true };
};
